/**
 * Services.jsx
 * ------------------------------------
 * Pure Vegetarian South Indian Food Services
 */

import React, { memo, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "../styles/Services.css";

/* ===================== DATA ===================== */
const SERVICES = [
  {
    id: "catering",
    route: "/catering",
    title: "Catering Services",
    subtitle: "Pure Veg Catering for All Occasions",
    description:
      "We provide authentic South Indian pure vegetarian catering for weddings, poojas, housewarming functions, corporate events, and family gatherings. Every meal is prepared traditionally with premium ingredients and strict hygiene.",
    highlights: [
      "Weddings & Reception",
      "Pooja & Religious Functions",
      "Corporate & Community Events",
      "Custom Menu Planning",
      "Bulk Orders with Quality Assurance"
    ]
  },
  {
    id: "madi-samayal",
    route: "/chef-booking",
    title: "Madi Samayal",
    subtitle: "Traditional Home Chef Services",
    description:
      "Our Madi Samayal service offers experienced home chefs who follow traditional cooking practices, perfect for households that value ritual purity, temple-style cooking, and authentic preparation methods.",
    highlights: [
      "Strict Traditional Cooking Methods",
      "Ideal for Pooja & Vratham Days",
      "Experienced Home Chefs",
      "Custom Household Requirements",
      "Pure Vegetarian & Sattvic Meals"
    ]
  },
  {
    id: "daily-foods",
    route: "/cloud-kitchen",
    title: "Daily Foods",
    subtitle: "Fresh Home-Style Daily Meals",
    description:
      "Enjoy freshly prepared South Indian vegetarian meals delivered daily. Ideal for working professionals, seniors, students, and families who want healthy, home-style food without compromise.",
    highlights: [
      "Freshly Cooked Every Day",
      "Balanced & Nutritious Meals",
      "Weekly & Monthly Plans",
      "Home-Style Taste",
      "Reliable Daily Delivery"
    ]
  }
];

const Services = () => {
  const navigate = useNavigate();

  const handleNavigate = useCallback(
    route => {
      navigate(route || "/services");
    },
    [navigate]
  );

  return (
    <section className="services-page">
      {/* Header */}
      <motion.header
        className="services-header"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1>Our Services</h1>
        <p>
          Taste of tradition, delivered fresh — explore our pure vegetarian
          South Indian food services.
        </p>
      </motion.header>

      {/* Services List */}
      <div className="services-container">
        {SERVICES.map((service, index) => (
          <motion.article
            key={service.id}
            className="service-card"
            role="button"
            tabIndex={0}
            aria-label={`Go to ${service.title}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4, scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
            onClick={() => handleNavigate(service.route)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleNavigate(service.route);
              }
            }}
          >
            <div className="service-content">
              <h2>{service.title}</h2>
              <h3>{service.subtitle}</h3>
              <p>{service.description}</p>

              <ul>
                {service.highlights.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>

              {/* Button (still works independently) */}
              <button
                className="service-action"
                onClick={e => {
                  e.stopPropagation(); // Prevent double navigation
                  handleNavigate(service.route);
                }}
              >
                Explore {service.title}
              </button>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
};

export default memo(Services);
